'use client'

import * as React from 'react'
import { cn } from '../../helpers/index.js'
import { Button, Badge, Card, CardContent, Picture, Text, Heading, Progress } from '../../components/index.js'

import type {
  ButtonFunctionType,
  CopyConfig,
  ProductCardData,
  StockDisplayMode,
  MediaShelfSemanticName,
} from './types.js'

/**
 * 超卖时库存条显示的最小百分比
 * 防止超卖时库存条显示为满格（100%）或完全空（0%）
 */
const OVERSELLING_STOCK_PERCENTAGE = 5

/**
 * 判断是否应该显示库存信息
 * @param stockDisplayMode 库存展示模式
 * @param stockThresholdValue 库存阈值（当模式为 below-quantity 或 below-percentage 时使用）
 * @param quantityAvailable 可用库存数量
 * @param totalInventory 总库存
 * @returns 是否显示库存信息
 */
function shouldShowStock(
  stockDisplayMode: StockDisplayMode | undefined,
  stockThresholdValue: number | undefined,
  quantityAvailable: number,
  totalInventory: number
): boolean {
  // 未配置时默认显示
  if (stockDisplayMode === undefined) {
    return true
  }

  // 根据展示模式判断
  switch (stockDisplayMode) {
    case 'always':
      // 总是显示
      return true

    case 'never':
      // 永不显示
      return false

    case 'below-quantity':
      // 当库存数量低于阈值时显示
      if (stockThresholdValue === undefined) {
        return true // 没有设置阈值时默认显示
      }

      return quantityAvailable <= stockThresholdValue

    case 'below-percentage':
      // 当库存百分比低于阈值时显示
      if (stockThresholdValue === undefined) {
        return true // 没有设置阈值时默认显示
      }
      return (quantityAvailable / totalInventory) * 100 <= stockThresholdValue

    default:
      // 未知模式时默认显示
      return true
  }
}

/**
 * MediaShelf ProductCard 组件 Props 接口
 */
export interface MediaShelfProductCardProps {
  /** 产品数据 */
  product: ProductCardData
  /** 样式类名 */
  className?: string
  /** 库存展示模式 */
  stockDisplayMode?: StockDisplayMode
  /** 库存阈值（当模式为 below-quantity 或 below-percentage 时使用） */
  stockThresholdValue?: number
  /** 点击了解更多回调 */
  onLearnMore?: (product: ProductCardData) => void
  /** 点击立即购买回调 */
  onShopNow?: (product: ProductCardData) => void
  /** 点击加入购物车回调 */
  onAddToCart?: (product: ProductCardData) => void
  /** 语义化类名 */
  classNames?: Partial<Record<MediaShelfSemanticName, string>>
  /** 了解更多按钮文本 */
  secondaryButtonText?: string
  /** 了解更多按钮功能 */
  secondaryButtonFun?: ButtonFunctionType
  /** 立即购买按钮文本 */
  primaryButtonText?: string
  /** 立即购买按钮功能 */
  primaryButtonFun?: ButtonFunctionType
  /** 是否展示原价（删除线价格） */
  showOriginalPrice?: boolean
  /** 文案配置 */
  copy?: CopyConfig
  /** 点击产品图片回调 */
  onProductImageClick?: (product: ProductCardData) => void
  /** 是否展示标签 */
  showTags?: boolean
}

/**
 * MediaShelf 产品卡片组件
 */
const ProductCard = React.forwardRef<HTMLDivElement, MediaShelfProductCardProps>(
  (
    {
      product,
      className,
      stockDisplayMode,
      stockThresholdValue,
      onLearnMore,
      onShopNow,
      onAddToCart,
      classNames,
      secondaryButtonText,
      secondaryButtonFun,
      primaryButtonText,
      primaryButtonFun,
      showOriginalPrice,
      copy,
      onProductImageClick,
      showTags = true,
    },
    ref
  ) => {
    const [primaryLoading, setPrimaryLoading] = React.useState(false)
    const [secondaryLoading, setSecondaryLoading] = React.useState(false)

    const stockPercentage = React.useMemo(() => {
      if (product?.availableForSale && product?.quantityAvailable <= 0) {
        return OVERSELLING_STOCK_PERCENTAGE
      }
      // 不可售时显示0%
      if (!product?.availableForSale) {
        return 0
      }
      return ((product?.quantityAvailable ?? 0) / (product?.totalInventory ?? 1)) * 100
    }, [product?.availableForSale, product?.quantityAvailable, product?.totalInventory])

    // 根据按钮功能类型调用相应的回调函数
    const handleButtonClick = async (buttonFun?: ButtonFunctionType, buttonType?: 'primary' | 'secondary') => {
      if (!buttonFun) return

      const setLoading = buttonType === 'primary' ? setPrimaryLoading : setSecondaryLoading
      setLoading(true)

      try {
        switch (buttonFun) {
          case 'buyNow':
            await onShopNow?.(product)
            break
          case 'addCart':
            await onAddToCart?.(product)
            break
          case 'learnMore':
            await onLearnMore?.(product)
            break
          default:
            break
        }
      } finally {
        setLoading(false)
      }
    }

    const overselling = product.availableForSale && product.quantityAvailable <= 0

    return (
      <Card
        ref={ref}
        className={cn(
          'tablet:min-w-[auto] hover:bg-container-secondary-0 flex h-full min-w-[296px] flex-col overflow-hidden border-none',
          product.custom_link ? 'cursor-pointer' : '',
          className,
          classNames?.productCard
        )}
      >
        {/* 图片区域 */}
        <div
          className={cn(
            'media-shelf-product-card-image-wrapper desktop:h-[240px] relative h-[200px] shrink-0 overflow-hidden',
            classNames?.productCardImageWrapper
          )}
        >
          {/* 背景图片 */}
          <div className={cn('media-shelf-product-card-image-bg absolute inset-0 ', classNames?.productCardImageBg)}>
            <a
              onClick={() => onProductImageClick?.(product)}
              {...(!onProductImageClick && {
                href: product.custom_link || product.listingLink,
              })}
              className="cursor-pointer"
            >
              <Picture
                source={product.custom_image || product.image}
                alt={product.name}
                className={cn('h-full', classNames?.productCardImage)}
                imgClassName="h-full object-contain"
              />
            </a>
          </div>

          {/* 标签区域 */}
          {showTags && product.tags && product.tags.length > 0 && (
            <div className="lg-desktop:h-[28px] absolute left-4 top-4 flex h-[24px]">
              {product.tags.map((tag: any, index) =>
                (tag as any).label ? (
                  <Badge
                    key={index}
                    size="sm"
                    variant={(tag.variant as any) || 'outline'}
                    className={cn('mr-1')}
                    promotionalType={tag.promotionalType}
                  >
                    {tag.label}
                  </Badge>
                ) : (
                  <React.Fragment key={index}>{tag}</React.Fragment>
                )
              )}
            </div>
          )}
        </div>

        {/* 内容区域 */}
        <CardContent className="desktop:p-6 bg-container-primary laptop:gap-6 flex h-[calc(100%-240px)] grow flex-col justify-between gap-4 p-4">
          {/* 产品标题和副标题 */}
          <div className="flex-0">
            <Heading
              as="h3"
              size={2}
              className={cn('text-info-primary mb-1 line-clamp-2 tracking-tight', classNames?.productTitle)}
            >
              {product.custom_name || product.name}
            </Heading>
            {(product.custom_description || product?.description) && (
              <Text
                size={2}
                className={cn('text-info-primary line-clamp-1', classNames?.productDescription)}
                html={product.custom_description || product.description}
              />
            )}
          </div>

          {/* 库存信息 */}
          {shouldShowStock(
            stockDisplayMode,
            stockThresholdValue,
            product?.quantityAvailable ?? 0,
            product?.totalInventory ?? 0
          ) && (
            <div
              data-total={product?.totalInventory ?? 0}
              data-available={product?.quantityAvailable ?? 0}
              className="space-y-2"
            >
              <Progress
                value={stockPercentage}
                max={100}
                min={0}
                size="base"
                variant="default"
                aria-label="stock progress"
                classNames={{
                  progressBar: 'transition-all duration-300 ease-in-out',
                }}
              />
              <Text size={3} className="text-info-tertiary desktop:text-[16px] lg-desktop:text-[18px] text-[12px]">
                {overselling
                  ? copy?.limitedStock
                  : copy?.stockDisplayText?.replace(
                      '{count}',
                      `${product.availableForSale ? product.quantityAvailable : 0}`
                    )}
              </Text>
            </div>
          )}

          <div>
            {/** 价格标签 */}
            {product?.priceLabel && product?.availableForSale && (
              <Text
                size={4}
                className={cn(
                  'text-marketing-1 desktop:text-[16px] lg-desktop:text-[18px] mt-2 text-[12px]',
                  classNames?.productPriceLabel
                )}
              >
                {product.priceLabel}
              </Text>
            )}
            {/* 价格区域 */}
            <div className={cn('mb-2', classNames?.productPrice)}>
              <div className="flex items-baseline gap-2">
                {product.availableForSale ? (
                  <>
                    <Heading size={2} className="text-info-primary" as="h6">
                      {product.price}
                    </Heading>
                    {showOriginalPrice && product.originalPrice && (
                      <Heading size={2} className="text-info-tertiary line-through" as="h6">
                        {product.originalPrice}
                      </Heading>
                    )}
                  </>
                ) : (
                  <Heading size={2} className="text-info-tertiary">
                    {copy?.outOfStockLabel ?? 'Sold Out'}
                  </Heading>
                )}
              </div>
            </div>

            {/* 按钮区域 */}
            <div className={cn('lg-desktop:gap-3 tablet:flex-nowrap flex flex-wrap gap-2', classNames?.buttonGroup)}>
              {secondaryButtonText && (
                <Button
                  variant="secondary"
                  size="base"
                  className={cn('tablet:w-fit w-full', classNames?.secondaryButton)}
                  onClick={() => handleButtonClick(secondaryButtonFun, 'secondary')}
                  disabled={!product.availableForSale && secondaryButtonFun !== 'learnMore'}
                  loading={secondaryLoading}
                  data-headless-type-name="MediaShelf#ProductCard"
                  data-headless-title-desc-button={`${product.custom_name || product.name}#${product.description || ''}#${secondaryButtonText}`}
                  data-headless-sku={product.sku}
                >
                  {secondaryButtonText}
                </Button>
              )}
              {primaryButtonText && (
                <Button
                  variant="primary"
                  size="base"
                  className={cn('tablet:w-fit w-full', classNames?.primaryButton)}
                  onClick={() => handleButtonClick(primaryButtonFun, 'primary')}
                  disabled={!product.availableForSale && primaryButtonFun !== 'learnMore'}
                  loading={primaryLoading}
                  data-headless-type-name="MediaShelf#ProductCard"
                  data-headless-title-desc-button={`${product.custom_name || product.name}#${product.description || ''}#${primaryButtonText}`}
                  data-headless-sku={product.sku}
                >
                  {primaryButtonText}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }
)

ProductCard.displayName = 'MediaShelf.ProductCard'

export default ProductCard
export { ProductCard }
